import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Leaf, Menu, X } from "lucide-react";
import { useLead } from "./lead-context";

const NAV = [
  { label: "The Garden", href: "#manifesto" },
  { label: "Features", href: "#features" },
  { label: "Gallery", href: "#gallery" },
  { label: "Savings", href: "#savings" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
];

export const Header = () => {
  const { openLead } = useLead();
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setMenu(false);
    const target = href === "#top" ? 0 : document.querySelector(href);
    if (target === null) return;
    if (lenis) {
      lenis.scrollTo(target, { offset: -72, duration: 1.1 });
    } else if (typeof target !== "number") {
      target.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-sg-bg/70 backdrop-blur-xl border-b border-sg-border/60"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          onClick={() => go("#top")}
          className="flex items-center gap-2.5 group"
          data-testid="logo-btn"
        >
          <span className="grid place-items-center w-8 h-8 rounded-full bg-sg-primary text-sg-bg">
            <Leaf size={16} strokeWidth={2.2} />
          </span>
          <span className="font-display text-lg tracking-tight text-sg-fg">
            MySolarGarden
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              data-testid={`nav-${n.href.slice(1)}`}
              className="text-sm text-sg-muted hover:text-sg-fg transition-colors duration-300 tracking-wide"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openLead("quote")}
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-sg-primary px-5 py-2.5 text-sm font-medium text-sg-bg hover:bg-sg-fg transition-colors duration-300"
          >
            Request a Quote
          </button>
          <button
            onClick={() => setMenu((m) => !m)}
            className="md:hidden text-sg-fg p-2"
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden overflow-hidden bg-sg-bg/95 backdrop-blur-xl border-b border-sg-border"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  onClick={() => go(n.href)}
                  className="text-left text-base text-sg-fg/90"
                >
                  {n.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setMenu(false);
                  openLead("quote");
                }}
                className="mt-2 rounded-full bg-sg-primary px-5 py-3 text-sm font-medium text-sg-bg"
              >
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
